import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api/axios";
import "./DealDetailsPage.css";
import { FaPhone, FaEnvelope, FaCheckCircle } from "react-icons/fa";
import { FiEdit, FiPhone, FiMail, FiMessageSquare, FiCalendar, FiVideo } from "react-icons/fi";
import { useAuth } from "../auth/AuthContext";

const openOutlookMeeting = ({ subject, body, attendees = [] }) => {
  const params = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`,
    `to=${encodeURIComponent(attendees.join(";"))}`,
  ].join("&");
  const url = `https://outlook.office.com/calendar/0/deeplink/compose?${params}`;
  window.open(url, "_blank");
};

const openOutlookEmail = ({ subject, body, to = "" }) => {
  const params = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`,
    `to=${encodeURIComponent(to)}`,
  ].join("&");
  const url = `https://outlook.office.com/mail/deeplink/compose?${params}`;
  window.open(url, "_blank");
};

const openTeamsMeeting = ({ subject, content = "" }) => {
  const params = [
    `subject=${encodeURIComponent(subject)}`,
    `content=${encodeURIComponent(content)}`,
  ].join("&");
  const url = `https://teams.microsoft.com/l/meeting/new?${params}`;
  window.open(url, "_blank");
};

const DealDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonError, setDeleteReasonError] = useState("");

  // Quick Actions state
  const [actionBusy, setActionBusy] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskDueAt, setTaskDueAt] = useState("");

  const [showLogModal, setShowLogModal] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editingActivityNote, setEditingActivityNote] = useState("");
  const [savingActivityEdit, setSavingActivityEdit] = useState(false);

  // Post-action confirmation modal (email/call/meet)
  const [pendingConfirm, setPendingConfirm] = useState(null); // { type, title, description }
  // eslint-disable-next-line no-unused-vars
  const [confirmLogging, setConfirmLogging] = useState(false);
  const [confirmNote, setConfirmNote] = useState("");

  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");

  // ===== Editable Attachment / Notes state =====
  const [attachmentText, setAttachmentText] = useState("");
  const [lastSavedText, setLastSavedText] = useState("");
  const [lastSavedJobTitle, setLastSavedJobTitle] = useState("");
  const [lastSavedJobSkills, setLastSavedJobSkills] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(""); // ISO string or whatever backend returns
  const [savingAttachment, setSavingAttachment] = useState(false);

  // toast
  const [showToast, setShowToast] = useState(false);

  // HireHut job creation state
  const [creatingJob, setCreatingJob] = useState(false);
  const [jobCreationError, setJobCreationError] = useState("");
  const [jobCreationSuccess, setJobCreationSuccess] = useState(false);
  const [jobTitle, setJobTitle] = useState("");
  const [jobSkillsInput, setJobSkillsInput] = useState("");
  const [lastCreatedJobSignature, setLastCreatedJobSignature] = useState("");

  const normalizedJobTitle = (jobTitle || "").trim();
  const normalizedJobSkills = (jobSkillsInput || "")
    .split(",")
    .map((skill) => skill.trim())
    .filter(Boolean);
  const normalizedJobDescription = (attachmentText || "").trim();
  const currentJobSignature = JSON.stringify({
    title: normalizedJobTitle,
    skills: normalizedJobSkills,
    description: normalizedJobDescription,
  });
  const hasCreatedForCurrentDetails =
    !!lastCreatedJobSignature && lastCreatedJobSignature === currentJobSignature;
  const isDirty =
    attachmentText !== lastSavedText ||
    jobTitle !== lastSavedJobTitle ||
    JSON.stringify(normalizedJobSkills) !== JSON.stringify(lastSavedJobSkills);
  const canCreateJob =
    !creatingJob && !hasCreatedForCurrentDetails;
  const createJobButtonTitle = creatingJob
    ? "Creating job..."
    : hasCreatedForCurrentDetails
      ? "Job already created for these details. Change title, skills, or JD to create again."
      : !canCreateJob
        ? "Add required title, skills, and description"
        : "Create HireHut Job";

  // Check if user can edit/delete this deal
  const currentUserId = user?.id || user?._id;
  const canEditDelete = user && (user.role === "admin" || currentUserId === deal?.created_by);

  useEffect(() => {
    const fetchDeal = async () => {
      setLoading(true);
      setError("");
      try {
        // Backend route is /api/deals/{deal_id} (no trailing slash)
        const response = await api.get(`/api/deals/${id}`);
        setDeal(response.data);

        const existingText =
          (response.data?.attachment && response.data.attachment.text) || "";
        setAttachmentText(existingText);
        setLastSavedText(existingText);
        const apiJobTitle = (response.data?.job_title || response.data?.jobTitle || "").trim();
        const apiSkillsRaw = response.data?.skills ?? response.data?.job_skills ?? response.data?.jobSkills ?? [];
        const apiSkills = Array.isArray(apiSkillsRaw)
          ? apiSkillsRaw
          : String(apiSkillsRaw || "")
              .split(",")
              .map((skill) => skill.trim())
              .filter(Boolean);

        let jobTitleValue = apiJobTitle;
        let skillsValue = apiSkills;

        // Fallback only when API does not provide job fields.
        // This guards against stale backend responses while still preferring DB/API values.
        if (!jobTitleValue && skillsValue.length === 0) {
          try {
            const localJobDetailsRaw = localStorage.getItem(`deal-job-details-${id}`) || "";
            if (localJobDetailsRaw) {
              const localJobDetails = JSON.parse(localJobDetailsRaw);
              jobTitleValue = (localJobDetails?.job_title || "").trim();
              skillsValue = Array.isArray(localJobDetails?.skills) ? localJobDetails.skills : [];
            }
          } catch (storageError) {
            console.warn("Unable to read cached job details from localStorage", storageError);
          }
        }

        setJobTitle(jobTitleValue);
        setLastSavedJobTitle(jobTitleValue);
        setJobSkillsInput(skillsValue.join(", "));
        setLastSavedJobSkills(skillsValue);
        try {
          const savedSignature = localStorage.getItem(`deal-job-created-signature-${id}`) || "";
          setLastCreatedJobSignature(savedSignature);
        } catch (storageError) {
          console.warn("Unable to read created job signature from localStorage", storageError);
          setLastCreatedJobSignature("");
        }
        setLastSavedAt(
          (response.data?.attachment &&
            (response.data.attachment.updated_at ||
              response.data.attachment.uploaded_at)) ||
          ""
        );
      } catch (err) {
        console.error("Failed to fetch deal", err);
        setError("Could not load deal details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchDeal();
  }, [id]);

  const formatPhone = (phone) => {
    if (!phone) return "N/A";
    const digits = String(phone).replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  };

  // Format stage string for display: replace _ with space and capitalize each word
  const formatStageLabel = (stage) => {
    if (!stage) return "—";
    return stage
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const stageLabel = useMemo(() => {
    return formatStageLabel(deal?.stage);
  }, [deal]);

  

  const stageColorClass = (stage) => {
    switch ((stage || "").toLowerCase()) {
      case "prospecting":
        return "stage-prospecting";
      case "qualification":
        return "stage-qualification";
      case "proposal":
        return "stage-proposal";
      case "closed_won":
        return "stage-closed-won";
      case "closed_lost":
        return "stage-closed-lost";
      default:
        return "stage-default";
    }
  };


  // ✅ delete flow: click Delete -> open modal; Yes -> delete; Cancel -> close modal
  const handleDelete = async (confirmed = false) => {
    if (deleting) return;

    if (!confirmed) {
      setDeleteReason("");
      setDeleteReasonError("");
      setShowDeleteConfirm(true);
      return;
    }

    const reason = (deleteReason || "").trim();
    if (reason.length < 3) {
      setDeleteReasonError("Please provide a short reason (at least 3 characters).");
      return;
    }

    setDeleting(true);
    setError("");
    setDeleteReasonError("");

    try {
      await api.delete(`/api/deals/${id}`, { data: { reason } });
      setShowDeleteConfirm(false);
      setDeleteReason("");
      navigate("/deals");
    } catch (err) {
      console.error("Failed to delete deal", err);
      const detail = err?.response?.data?.detail || "Failed to delete the deal. Please try again.";
      setError(String(detail));
      setDeleting(false);
      // keep modal open so user can retry or cancel
    }
  };

  const createQuickActionActivity = async (activity_type, title, description = "") => {
    await api.post(`/api/deals/${id}/quick-actions/activity`, {
      activity_type,
      title,
      description,
    });
  };

  // Opens external app then shows "Did you complete it?" confirmation.
  // Logging only happens if user clicks Yes in the popup.
  const openExternalAndConfirm = (openFn, confirmPayload) => {
    openFn();
    setPendingConfirm(confirmPayload);
  };

  // eslint-disable-next-line no-unused-vars
  const handleConfirmLog = async () => {
    if (!pendingConfirm) return;
    setConfirmLogging(true);
    try {
      const noteText = confirmNote.trim();
      const fullDescription = noteText
        ? `${pendingConfirm.description}\n\nNote: ${noteText}`
        : pendingConfirm.description;
      await createQuickActionActivity(
        pendingConfirm.type,
        pendingConfirm.title,
        fullDescription,
      );
      setPendingConfirm(null);
      setConfirmNote("");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      console.error("Failed to log activity", err);
    } finally {
      setConfirmLogging(false);
    }
  };

  const handleSaveActivityEdit = async (activityId) => {
    setSavingActivityEdit(true);
    try {
      await api.patch(`/api/deals/${id}/quick-actions/activity/${activityId}`, {
        description: editingActivityNote,
      });
      // Update local timeline state so UI reflects change immediately
      setTimeline((prev) =>
        prev.map((item) =>
          item.id === activityId ? { ...item, description: editingActivityNote } : item
        )
      );
      setEditingActivityId(null);
      setEditingActivityNote("");
    } catch (err) {
      console.error("Failed to save activity edit", err);
    } finally {
      setSavingActivityEdit(false);
    }
  };

  const handleSaveNote = async () => {
    const content = (noteContent || "").trim();
    if (!content) {
      setError("Please enter a note before saving.");
      return;
    }
    setActionBusy(true);
    setError("");
    try {
      await api.post(`/api/deals/${id}/quick-actions/note`, {
        title: (noteTitle || "").trim() || undefined,
        content,
      });
      setShowNoteModal(false);
      setNoteTitle("");
      setNoteContent("");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      console.error("Failed to save note", err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to save note. Please try again.";
      setError(String(detail));
    } finally {
      setActionBusy(false);
    }
  };

  const handleCreateTask = async () => {
    if (!(taskTitle || "").trim() || !taskDueAt) {
      setError("Task title and due date/time are required.");
      return;
    }
    setActionBusy(true);
    setError("");
    try {
      await api.post(`/api/deals/${id}/quick-actions/task`, {
        title: taskTitle.trim(),
        description: (taskDescription || "").trim() || undefined,
        due_at: new Date(taskDueAt).toISOString(),
      });
      setShowTaskModal(false);
      setTaskTitle("");
      setTaskDescription("");
      setTaskDueAt("");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      console.error("Failed to create task", err);
      setError("Failed to create task reminder. Please try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleOpenLog = async () => {
    setShowLogModal(true);
    setTimelineLoading(true);
    setTimelineError("");
    try {
      const res = await api.get(`/api/deals/${id}/quick-actions/timeline`);
      const items = Array.isArray(res.data) ? res.data : [];

      // Some environments can still return both audit and quick-action rows.
      // Prefer canonical quick-action rows so each activity appears once.
      const canonicalRows = items.filter(
        (item) => String(item?.source || "").toLowerCase() !== "activity_log"
      );

      const uniqueRows = (canonicalRows.length > 0 ? canonicalRows : items).filter(
        (item, index, arr) =>
          arr.findIndex((x) => `${x?.source}-${x?.id}` === `${item?.source}-${item?.id}`) === index
      );

      setTimeline(uniqueRows);
    } catch (err) {
      console.error("Failed to load timeline", err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to load activity timeline.";
      setTimelineError(String(detail));
    } finally {
      setTimelineLoading(false);
    }
  };

  const getTimelineHeadline = (item) => {
    const actor = item?.user_name || "Unknown user";
    const type = String(item?.activity_type || "").toLowerCase();

    if (type === "note") return `${actor} added a note`;
    if (type === "email") return `${actor} sent an email`;
    if (type === "call") return `${actor} made a call`;
    if (type === "meet") return `${actor} scheduled a meeting`;
    if (type === "task") return `${actor} created a task`;
    if (type === "log") return `${actor} logged an activity`;
    return `${actor} updated deal activity`;
  };

  const getTimelineDetails = (item) => {
    const type = String(item?.activity_type || "").toLowerCase();
    const title = (item?.title || "").trim();
    const description = (item?.description || "").trim();

    if (type === "note") {
      return description || title;
    }

    if (type === "task") {
      if (title && description) return `${title}\n${description}`;
      return description || title;
    }

    return description || title;
  };

  const handleEmail = () => {
    if (!deal) return;
    openExternalAndConfirm(
      () => openOutlookEmail({
        to: primaryContact?.email || "",
        subject: `Deal: ${deal.title}`,
        body: `Hello,\n\nRegarding deal "${deal.title}".\n\n`,
      }),
      {
        type: "email",
        title: `Email — ${deal.title}`,
        description: `Sent email regarding deal "${deal.title}"${primaryContact?.email ? ` to ${primaryContact.email}` : ""}.`,
      }
    );
  };

  const handleMeet = () => {
    if (!deal) return;
    openExternalAndConfirm(
      () => openOutlookMeeting({
        subject: `Deal Meeting: ${deal.title}`,
        body: `Discussion for deal "${deal.title}".`,
        attendees: primaryContact?.email ? [primaryContact.email] : [],
      }),
      {
        type: "meet",
        title: `Meeting — ${deal.title}`,
        description: `Scheduled a meeting for deal "${deal.title}"${primaryContact?.email ? ` with ${primaryContact.email}` : ""}.`,
      }
    );
  };

  const handleCall = () => {
    if (!deal) return;
    openExternalAndConfirm(
      () => openTeamsMeeting({
        subject: `Call: ${deal.title}`,
        content: `Call planning for deal "${deal.title}".`,
      }),
      {
        type: "call",
        title: `Call — ${deal.title}`,
        description: `Had a call regarding deal "${deal.title}"${primaryContact?.name ? ` with ${primaryContact.name}` : ""}.`,
      }
    );
  };

  const handleAttachmentSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setAttachmentError("");

    const name = (file.name || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";

    // Block images explicitly
    if ((file.type || "").startsWith("image/")) {
      setAttachmentError(
        "Images are not supported. Please upload a document file."
      );
      e.target.value = "";
      return;
    }

    const allowed = ["txt", "pdf", "docx", "doc"];
    if (!allowed.includes(ext)) {
      setAttachmentError(
        "Unsupported file type. Allowed: .txt, .pdf, .docx, .doc"
      );
      e.target.value = "";
      return;
    }

    setAttachmentUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      // Don't set Content-Type - let browser set it with proper boundary
      const res = await api.post(`/api/deals/${id}/attachment`, fd, {
        headers: {
          'Content-Type': undefined
        }
      });

      console.log("Upload response:", res.data);
      console.log("Attachment text:", res.data?.attachment?.text);

      // backend returns updated deal
      setDeal(res.data);

      const newText = (res.data?.attachment && res.data.attachment.text) || "";
      setAttachmentText(newText);
      setLastSavedText(newText);
      setLastSavedAt(
        (res.data?.attachment &&
          (res.data.attachment.updated_at || res.data.attachment.uploaded_at)) ||
        ""
      );

      // toast
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      console.error("Failed to upload attachment", err);
      console.error("Error response:", err.response?.data);
      console.error("Error detail:", JSON.stringify(err.response?.data?.detail, null, 2));
      console.error("Error status:", err.response?.status);
      setAttachmentError("Failed to upload document. Please try again.");
    } finally {
      setAttachmentUploading(false);
      e.target.value = "";
    }
  };

  // Create HireHut job from deal attachment
  const handleCreateJob = async () => {
    if (creatingJob) return;

    if (!normalizedJobTitle) {
      setJobCreationError("Job title is required.");
      setTimeout(() => setJobCreationError(""), 5000);
      return;
    }

    if (normalizedJobSkills.length === 0) {
      setJobCreationError("At least one skill is required. Enter comma-separated skills.");
      setTimeout(() => setJobCreationError(""), 5000);
      return;
    }

    // Existing attachment text is the job description
    if (!attachmentText || attachmentText.trim() === "") {
      setJobCreationError("Job description is required. Add or attach document text first.");
      setTimeout(() => setJobCreationError(""), 5000);
      return;
    }

    setCreatingJob(true);
    setJobCreationError("");
    setJobCreationSuccess(false);

    try {
      // Call backend proxy with the new HireHut contract fields
      const response = await api.post(
        "/api/hirehut/createjob",
        {
          title: normalizedJobTitle,
          skills: normalizedJobSkills,
          description: attachmentText,
        },
        {
          // Keep user on page for this action so backend auth/config errors are visible in UI.
          skipAuthRedirect: true,
        }
      );

      if (response.data.success) {
        try {
          localStorage.setItem(`deal-job-created-signature-${id}`, currentJobSignature);
        } catch (storageError) {
          console.warn("Unable to persist created job signature", storageError);
        }
        setLastCreatedJobSignature(currentJobSignature);
        setJobCreationSuccess(true);
        setTimeout(() => setJobCreationSuccess(false), 3000);
      } else {
        throw new Error(response.data.message || "Failed to create job");
      }
    } catch (err) {
      console.error("Failed to create HireHut job", err);
      const statusCode = err.response?.status;
      const rawDetail = err.response?.data?.detail;
      const detailText =
        typeof rawDetail === "string"
          ? rawDetail
          : rawDetail
            ? JSON.stringify(rawDetail)
            : "";

      const errorMsg =
        statusCode && detailText
          ? `[${statusCode}] ${detailText}`
          : detailText || "Unable to create job right now. Please try again.";

      setJobCreationError(errorMsg);
      setTimeout(() => setJobCreationError(""), 15000);
    } finally {
      setCreatingJob(false);
    }
  };

  // Save all job details in one go (job title, skills, and description)
  const handleSaveAllJobDetails = async () => {
    if (savingAttachment || !isDirty) return;

    setSavingAttachment(true);
    setAttachmentError("");
    setError("");

    try {
      let updatedDeal = null;

      // First, save attachment text if changed
      if (attachmentText !== lastSavedText) {
        const textRes = await api.put(`/api/deals/${id}/attachment`, {
          text: attachmentText,
        });
        updatedDeal = textRes.data;
      }

      // Then, save job details if changed
      if (
        jobTitle !== lastSavedJobTitle ||
        JSON.stringify(normalizedJobSkills) !== JSON.stringify(lastSavedJobSkills)
      ) {
        const detailsRes = await api.put(`/api/deals/${id}`, {
          job_title: normalizedJobTitle || null,
          skills: normalizedJobSkills,
        });
        updatedDeal = detailsRes.data;
      }

      // Update the deal if either endpoint was called
      if (updatedDeal) {
        setDeal(updatedDeal);
      }

      // Update saved states
      setLastSavedText(attachmentText);
      setLastSavedJobTitle(normalizedJobTitle);
      setLastSavedJobSkills(normalizedJobSkills);
      setLastSavedAt(new Date().toISOString());

      // Cache last saved job fields for resilient hydration if API omits fields.
      try {
        localStorage.setItem(
          `deal-job-details-${id}`,
          JSON.stringify({
            job_title: normalizedJobTitle,
            skills: normalizedJobSkills,
          })
        );
      } catch (storageError) {
        console.warn("Unable to cache saved job details", storageError);
      }

      // toast
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      console.error("Failed to save job details", err);
      const errorMsg =
        err.response?.data?.detail || "Failed to save. Please try again.";
      if (err.message?.includes("attachment")) {
        setAttachmentError(String(errorMsg));
      } else {
        setError(String(errorMsg));
      }
    } finally {
      setSavingAttachment(false);
    }
  };

  if (loading) {
    return (
      <div className="deal-details-page">
        <div className="deal-shell">
          <div className="deal-topbar">
            <div className="skeleton skeleton-line" style={{ width: 160 }} />
            <div className="deal-actions">
              <div className="skeleton skeleton-btn" />
              <div className="skeleton skeleton-btn" />
            </div>
          </div>
          <div className="card">
            <div className="skeleton skeleton-title" />
            <div className="skeleton skeleton-line" />
            <div className="skeleton skeleton-line" style={{ width: "70%" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="deal-details-page">
        <div className="deal-shell">
          <div className="card">
            <h2 className="section-title">Deal not found</h2>
            <p className="muted">{error || "We couldn’t find that deal."}</p>
            <div className="deal-actions" style={{ marginTop: 16 }}>
              <Link to="/deals" className="btn btn-secondary">
                Back to Deals
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const primaryContact = deal.contacts?.length ? deal.contacts[0] : null;

  return (
    <div className="deal-details-page">
      <div className="deal-shell">
        <div className="deal-topbar">
          <Link to="/deals" className="back-link">
            &larr; Back to Deals
          </Link>

          <div className="deal-actions">
            

            {canEditDelete ? (
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <Link 
                  to={`/deals/edit/${deal.id}`} 
                  style={{
                    background: "#2563eb",
                    color: "white",
                    padding: "10px 24px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    transition: "all 0.2s",
                    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    minWidth: "120px"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
                  }}
                >
                  Edit Deal
                </Link>

                <button
                  type="button"
                  onClick={() => handleDelete(false)}
                  disabled={deleting}
                  style={{
                    background: "#dc2626",
                    color: "white",
                    padding: "10px 24px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: deleting ? "not-allowed" : "pointer",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    transition: "all 0.2s",
                    boxShadow: "0 4px 12px rgba(245, 87, 108, 0.3)",
                    opacity: deleting ? 0.7 : 1,
                    whiteSpace: "nowrap",
                    minWidth: "120px"
                  }}
                  onMouseOver={(e) => {
                    if (!deleting) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 6px 16px rgba(245, 87, 108, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!deleting) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(245, 87, 108, 0.3)";
                    }
                  }}
                  title={deleting ? "Deleting..." : "Delete deal"}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {error ? (
          <div className="alert">
            <span>{error}</span>
          </div>
        ) : null}

        <div className="card deal-hero">
          <div className="deal-hero-top">
            <div className="deal-title-wrap">
              <h1 className="deal-title">{deal.title}</h1>
              <div className="deal-stage-container">
                <span className={`deal-stage ${stageColorClass(deal.stage)}`} aria-hidden="true"></span>
                <span className="deal-stage-text">{stageLabel}</span>
              </div>

            </div>

            <div className="deal-amount-wrap">
              <div className="label">Amount</div>
              <div className="deal-amount">
                ${parseFloat(deal.amount || 0).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="deal-summary-grid">
            <div className="summary-item">
              <div className="label">Company</div>
              <div className="value">{deal.company_name || "—"}</div>
            </div>

            <div className="summary-item">
              <div className="label">Expected Close</div>
              <div className="value">{formatDate(deal.close_date)}</div>
            </div>

            <div className="summary-item">
              <div className="label">Created</div>
              <div className="value">{formatDate(deal.created_at)}</div>
            </div>

            

            <div className="summary-item">
              <div className="label">Deal ID</div>
              <div className="value muted">{deal.id}</div>
            </div>
          </div>
        </div>

        <div className="deal-sections">
          <div className="card section-card">
            <div className="section-head">
              <h2 className="section-title">Primary Contact</h2>
              <p className="muted">Main point of contact for this deal.</p>
            </div>

            {primaryContact ? (
              <div className="contact-card">
                <div className="avatar">
                  {primaryContact.avatar_url ? (
                    <img
                      src={primaryContact.avatar_url}
                      alt={primaryContact.name}
                    />
                  ) : (
                    <span className="avatar-fallback">
                      {(primaryContact.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="contact-details">
                  <div className="contact-name">{primaryContact.name}</div>
                  <div className="muted">{primaryContact.position || "—"}</div>

                  <div className="contact-links">
                    {primaryContact.phone_number ? (
                      <a href={`tel:${primaryContact.phone_number}`}>
                        <FaPhone /> {formatPhone(primaryContact.phone_number)}
                      </a>
                    ) : null}

                    {primaryContact.email ? (
                      <a href={`mailto:${primaryContact.email}`}>
                        <FaEnvelope /> {primaryContact.email}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p className="muted">No contact assigned</p>
              </div>
            )}
          </div>

          {/* ===== Quick Actions / Icons Card ===== */}
          <div className="card section-card">
            <div className="section-head">
              <h2 className="section-title">Quick Actions</h2>
              <p className="muted">Take action on this deal.</p>
            </div>

            <div className="action-icons-grid">
              <button className="action-icon-btn" title="Add Note" onClick={() => setShowNoteModal(true)}>
                <FiEdit size={20} />
                <span>Note</span>
              </button>
              <button className="action-icon-btn" title="Email" onClick={handleEmail}>
                <FiMail size={20} />
                <span>Email</span>
              </button>
              <button className="action-icon-btn" title="Call" onClick={handleCall}>
                <FiPhone size={20} />
                <span>Call</span>
              </button>
              <button className="action-icon-btn" title="Log" onClick={handleOpenLog}>
                <FiMessageSquare size={20} />
                <span>Log</span>
              </button>
              <button className="action-icon-btn" title="Task" onClick={() => setShowTaskModal(true)}>
                <FiCalendar size={20} />
                <span>Task</span>
              </button>
              <button className="action-icon-btn" title="Meet" onClick={handleMeet}>
                <FiVideo size={20} />
                <span>Meet</span>
              </button>
            </div>
          </div>

          {/* ===== Job Details & Description (editable + save + toast) ===== */}
          <div className="card section-card attachment-card">
            <div className="section-head attachment-head">
              <div>
                <h2 className="section-title">Job Details & Description</h2>
                <p className="muted">
                  Attach a .pdf, .docx, .doc, or .txt file. We’ll extract and
                  save the text for quick reference.
                </p>

                <div className="muted small" style={{ marginTop: 6 }}>
                  {isDirty ? (
                    <span>
                      <b>Unsaved changes</b>
                    </span>
                  ) : lastSavedAt ? (
                    <span>
                      Last saved: <b>{lastSavedAt}</b>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="attachment-actions">
                <input
                  id="deal-attach-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleAttachmentSelected}
                  style={{ display: "none" }}
                  disabled={attachmentUploading}
                />
                <label
                  htmlFor="deal-attach-input"
                  className={
                    "attach-btn" + (attachmentUploading ? " disabled" : "")
                  }
                  title={attachmentUploading ? "Uploading..." : "Attach a document"}
                >
                  {attachmentUploading ? "Uploading..." : "Attach document"}
                </label>
              </div>
            </div>

            {attachmentError ? (
              <div className="attachment-error">{attachmentError}</div>
            ) : null}

            {deal.attachment && deal.attachment.text ? (
              <div className="attachment-meta">
                <div className="muted small">
                  <span className="attachment-meta-label">File:</span>{" "}
                  <b>{deal.attachment.filename}</b>
                  {deal.attachment.uploaded_at ? (
                    <>
                      {" "}
                      •{" "}
                      <span className="attachment-meta-label">Uploaded:</span>{" "}
                      {deal.attachment.uploaded_at}
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p className="muted">No document attached</p>
              </div>
            )}

            <div className="attachment-view">
              <div className="job-fields">
                <div className="job-field">
                  <label className="job-field-label" htmlFor="job-title-input">
                    Job Title *
                  </label>
                  <input
                    id="job-title-input"
                    type="text"
                    className="job-field-input"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. Software Engineer"
                  />
                </div>
                <div className="job-field">
                  <label className="job-field-label" htmlFor="job-skills-input">
                    Skills *
                  </label>
                  <input
                    id="job-skills-input"
                    type="text"
                    className="job-field-input"
                    value={jobSkillsInput}
                    onChange={(e) => setJobSkillsInput(e.target.value)}
                    placeholder="e.g. JavaScript, TypeScript"
                  />
                  <div className="muted small">Enter comma-separated skills.</div>
                </div>
              </div>

              <label className="job-field-label" htmlFor="job-description-input">
                Job Description * (Attached Document)
              </label>
              <textarea
                id="job-description-input"
                className="attachment-text"
                value={attachmentText}
                onChange={(e) => setAttachmentText(e.target.value)}
                placeholder="Type here or attach a file to extract text..."
              />
            </div>

            <div className="attachment-footer">
              <button
                type="button"
                className={
                  "attach-save-btn" +
                  (!isDirty || savingAttachment ? " disabled" : "")
                }
                onClick={handleSaveAllJobDetails}
                disabled={!isDirty || savingAttachment}
                aria-disabled={!isDirty || savingAttachment}
                title={!isDirty ? "No changes to save" : "Save all job details"}
              >
                {savingAttachment ? "Saving..." : "Save"}
              </button>

              {/* Create Job button - visible to admin and executive only */}
              {user && (user.role === 'admin' || user.role === 'executive') && (
                <button
                  type="button"
                  className={"attach-save-btn" + (!canCreateJob ? " disabled" : "")}
                  onClick={handleCreateJob}
                  disabled={!canCreateJob}
                  aria-disabled={!canCreateJob}
                  title={createJobButtonTitle}
                  style={{
                    marginLeft: "10px",
                    background: "#10b981",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)"
                  }}
                >
                  {creatingJob ? "Creating Job..." : "Create Job"}
                </button>
              )}
            </div>

            {jobCreationError && (
              <div className="attachment-error" style={{ marginTop: "10px" }}>
                {jobCreationError}
              </div>
            )}

            {jobCreationSuccess && (
              <div className="attachment-toast" role="status" aria-live="polite" style={{ marginTop: "10px" }}>
                <FaCheckCircle className="toast-icon" />
                Job Created Successfully
              </div>
            )}

            {showToast ? (
              <div className="attachment-toast" role="status" aria-live="polite">
                <FaCheckCircle className="toast-icon" />
                Saved Successfully
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* ✅ Confirmation popup */}
      {showDeleteConfirm ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal">
            <div className="confirm-title">Confirm Delete</div>
            <div className="confirm-text">
              Please provide a reason for deleting this deal.
            </div>
            <textarea
              className="quick-textarea"
              placeholder="Reason for deleting this deal"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={4}
              disabled={deleting}
              style={{ minHeight: "100px", marginTop: "10px" }}
            />
            {deleteReasonError ? (
              <div className="attachment-error" style={{ marginTop: "8px" }}>{deleteReasonError}</div>
            ) : null}

            <div className="confirm-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteReasonError("");
                }}
                disabled={deleting}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleDelete(true)}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete Deal"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Quick Action - Note Modal */}
      {showNoteModal ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal quick-modal">
            <div className="confirm-title">Add Note</div>
            <div className="quick-form">
              <input
                type="text"
                className="quick-input"
                placeholder="Optional note title"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
              />
              <textarea
                className="quick-textarea"
                placeholder="Write your note here..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
              />
            </div>
            <div className="confirm-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowNoteModal(false)} disabled={actionBusy}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveNote} disabled={actionBusy}>
                {actionBusy ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Quick Action - Task Modal */}
      {showTaskModal ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal quick-modal">
            <div className="confirm-title">Create Task Reminder</div>
            <div className="quick-form">
              <input
                type="text"
                className="quick-input"
                placeholder="Task title"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
              />
              <textarea
                className="quick-textarea"
                placeholder="Task description (optional)"
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
              />
              <input
                type="datetime-local"
                className="quick-input"
                value={taskDueAt}
                onChange={(e) => setTaskDueAt(e.target.value)}
              />
            </div>
            <div className="confirm-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowTaskModal(false)} disabled={actionBusy}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCreateTask} disabled={actionBusy}>
                {actionBusy ? "Creating..." : "Create Reminder"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Quick Action - Combined Timeline Modal */}
      {showLogModal ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal quick-modal quick-log-modal">
            <div className="confirm-title">Deal Activity Timeline</div>
            <div className="quick-log-wrap">
              {timelineLoading ? <p className="muted">Loading timeline...</p> : null}
              {timelineError ? <p className="attachment-error">{timelineError}</p> : null}
              {!timelineLoading && !timelineError && timeline.length === 0 ? (
                <p className="muted">No activity logged yet.</p>
              ) : null}
              {!timelineLoading && !timelineError && timeline.length > 0 ? (
                <div className="quick-log-list">
                  {timeline.map((item) => {
                    const currentUserId = user?.id || user?._id;
                    const type = String(item?.activity_type || "").toLowerCase();
                    // Owner check: match by ID if present, else fall back to name match
                    const isOwner = currentUserId
                      ? item.created_by_id
                        ? item.created_by_id === currentUserId
                        : (item.user_name && item.user_name === (user?.full_name || user?.username))
                      : false;
                    const isEditing = editingActivityId === item.id;

                    const typeIcon = { note: "📝", email: "📧", call: "📞", meet: "🗓", task: "✅", log: "📋" }[type] || "📋";
                    const typeLabel = { note: "Note", email: "Email", call: "Call", meet: "Meeting", task: "Task", log: "Log" }[type] || type;
                    const badgeClass = `activity-badge activity-badge-${type || "log"}`;

                    const formattedDate = (() => {
                      try {
                        const d = new Date(item.created_at);
                        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                          + " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                      } catch { return item.created_at; }
                    })();

                    return (
                    <div key={`${item.source}-${item.id}`} className="quick-log-item">
                      {/* Card header: badge + actor (left) | time + edit (right) */}
                      <div className="quick-log-head">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span className={badgeClass}>{typeIcon} {typeLabel}</span>
                          <span className="quick-log-actor">{item.user_name || "Unknown"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                          <span className="quick-log-time">{formattedDate}</span>
                          {isOwner && !isEditing && (
                            <button
                              type="button"
                              onClick={() => { setEditingActivityId(item.id); setEditingActivityNote(item.description || ""); }}
                              style={{
                                background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: "6px",
                                cursor: "pointer", color: "#374151", fontSize: "11px", fontWeight: 500,
                                padding: "2px 8px", lineHeight: 1.4, whiteSpace: "nowrap",
                              }}
                              title="Edit this activity"
                            >
                              ✏ Edit
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Content */}
                      {isEditing ? (
                        <div style={{ marginTop: "0.6rem" }}>
                          <textarea
                            value={editingActivityNote}
                            onChange={(e) => setEditingActivityNote(e.target.value)}
                            rows={3}
                            disabled={savingActivityEdit}
                            style={{
                              width: "100%", resize: "vertical", borderRadius: "6px",
                              border: "1px solid #d1d5db", padding: "0.4rem 0.6rem",
                              fontSize: "0.85rem", fontFamily: "inherit",
                              boxSizing: "border-box", marginBottom: "0.4rem",
                            }}
                          />
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{ fontSize: "0.8rem", padding: "0.25rem 0.75rem" }}
                              onClick={() => handleSaveActivityEdit(item.id)}
                              disabled={savingActivityEdit}
                            >
                              {savingActivityEdit ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ fontSize: "0.8rem", padding: "0.25rem 0.75rem" }}
                              onClick={() => { setEditingActivityId(null); setEditingActivityNote(""); }}
                              disabled={savingActivityEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        getTimelineDetails(item)
                          ? <div className="quick-log-desc" style={{ marginTop: "6px" }}>{getTimelineDetails(item)}</div>
                          : null
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <div className="confirm-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowLogModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Post-action confirmation modal: log email / call / meet only when user confirms */}
      {pendingConfirm ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal quick-modal">
            <div className="confirm-title">
              {pendingConfirm.type === "email" && "Did you send the email?"}
              {pendingConfirm.type === "meet" && "Was the meeting scheduled?"}
              {pendingConfirm.type === "call" && "Did the call take place?"}
            </div>
            <p className="muted" style={{ margin: "0 0 1rem", fontSize: "0.9rem" }}>
              {pendingConfirm.description}
            </p>
            <p className="muted" style={{ margin: "0 0 1rem", fontSize: "0.85rem" }}>
              Click <b>Yes, Log it</b> to record this activity in the deal timeline. Click <b>Skip</b> if the action was not completed.
            </p>
            <textarea
              placeholder="Add a note (optional) — e.g. what the email was about, key points from the call…"
              value={confirmNote}
              onChange={e => setConfirmNote(e.target.value)}
              disabled={confirmLogging}
              rows={3}
              style={{
                width: "100%",
                resize: "vertical",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                padding: "0.5rem 0.75rem",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                marginBottom: "1.25rem",
                boxSizing: "border-box",
                outline: "none",
                color: "#111827",
              }}
            />
            <div className="confirm-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setPendingConfirm(null); setConfirmNote(""); }}
                disabled={confirmLogging}
              >
                Skip
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmLog}
                disabled={confirmLogging}
              >
                {confirmLogging ? "Logging..." : "Yes, Log it"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DealDetailsPage;
